import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { isContiguous, PAUSE_MS, segnoAtTime, type PlaybackRange } from '../lib/segmentQueue'
import { youtubeVideoId } from '../lib/youtube'
import { clampSeekTime } from '../lib/playbackSeek'
import { loadYouTubeIframeApi, type YTPlayer } from '../lib/youtubeIframeApi'
import type { Segno } from '../types'
import { PlaybackSeekControls } from './PlaybackSeekControls'
import { useI18n } from '../i18n/useI18n'

export interface YouTubePracticeHandle {
  startLoop: () => void
  stop: () => void
}

interface Props {
  youtubeUrl: string
  title: string
  queue: Segno[]
  segnos: Segno[]
  continuousRange: PlaybackRange | null
  activeOrder: number | null
  onActiveOrder: (order: number) => void
  requestWakeLock: () => Promise<boolean>
  onLoopingChange: (looping: boolean) => void
  onPlayingChange?: (playing: boolean) => void
  volume: number
  compactPortrait?: boolean
}

export const YouTubePractice = forwardRef<YouTubePracticeHandle, Props>(function YouTubePractice({ youtubeUrl, title, queue, segnos, continuousRange, activeOrder, onActiveOrder, requestWakeLock, onLoopingChange, onPlayingChange, volume, compactPortrait = false }, ref) {
  const { t } = useI18n()
  const hostRef = useRef<HTMLDivElement | null>(null)
  const playerRef = useRef<YTPlayer | null>(null)
  const transitionRef = useRef(false)
  const pauseTimerRef = useRef<number | null>(null)
  const pollRef = useRef<number | null>(null)
  const queueRef = useRef(queue)
  const segnosRef = useRef(segnos)
  const continuousRangeRef = useRef(continuousRange)
  const activeOrderRef = useRef(activeOrder)
  const [ready, setReady] = useState(false)
  const [looping, setLooping] = useState(false)
  const [message, setMessage] = useState('')
  const [touchLocked, setTouchLocked] = useState(false)
  // 연습 화면에 들어올 때마다 안내를 한 번 보여주고, 확인 후에는 현재 화면에서만 숨깁니다.
  const [showGuidance, setShowGuidance] = useState(true)
  const videoId = useMemo(() => youtubeVideoId(youtubeUrl), [youtubeUrl])
  const activeSegno = useMemo(
    () => segnos.find((segno) => segno.order === activeOrder) ?? queue[0] ?? null,
    [activeOrder, queue, segnos],
  )

  // 곡 전환/화면 이탈 시 이전 YouTube 세션이 실제 재생 중으로 남지 않도록 정리합니다.
  useEffect(() => () => onPlayingChange?.(false), [onPlayingChange])

  useEffect(() => { queueRef.current = queue }, [queue])
  useEffect(() => { segnosRef.current = segnos }, [segnos])
  useEffect(() => { continuousRangeRef.current = continuousRange }, [continuousRange])
  useEffect(() => { activeOrderRef.current = activeOrder }, [activeOrder])
  useEffect(() => { onLoopingChange(looping) }, [looping, onLoopingChange])
  useImperativeHandle(ref, () => ({
    startLoop: () => { void startLoop() },
    stop,
  }))
  useEffect(() => {
    if (ready) playerRef.current?.setVolume(volume)
  }, [ready, volume])

  useEffect(() => {
    if (!videoId || !hostRef.current) {
      setMessage(t('practice.youtube.invalidUrl'))
      return
    }
    let cancelled = false
    void loadYouTubeIframeApi().then((YT) => {
      if (cancelled || !hostRef.current) return
      const mountNode = document.createElement('div')
      hostRef.current.replaceChildren(mountNode)
      playerRef.current = new YT.Player(mountNode, {
        videoId,
        playerVars: { controls: 0, playsinline: 1, rel: 0, modestbranding: 1 },
        events: {
          onReady: () => {
            if (!cancelled) {
              playerRef.current?.setVolume(volume)
              setReady(true)
              setMessage('')
            }
          },
          onStateChange: (event) => {
            if (event.data === YT.PlayerState.PLAYING) {
              if (!cancelled) {
                setMessage('')
                onPlayingChange?.(true)
              }
            } else if (event.data === YT.PlayerState.PAUSED || event.data === YT.PlayerState.ENDED) {
              if (!cancelled) onPlayingChange?.(false)
            }
          },
          onError: () => {
            if (!cancelled) {
              setReady(false)
              setMessage(t('practice.youtube.cannotPlay'))
            }
          },
        },
      })
    }).catch(() => setMessage(t('practice.youtube.playerLoadFailed')))

    return () => {
      cancelled = true
      if (pauseTimerRef.current) window.clearTimeout(pauseTimerRef.current)
      if (pollRef.current) window.clearInterval(pollRef.current)
      playerRef.current?.destroy()
      playerRef.current = null
      hostRef.current?.replaceChildren()
    }
  }, [videoId])

  useEffect(() => {
    if (!looping) return
    const player = playerRef.current
    const first = queue[0]
    if (!first) {
      stop()
      return
    }
    if (!player || !ready) return

    // 세뇨 목록 화살표나 체크박스로 선택이 바뀌면
    // 현재 반복 상태를 유지한 채 새 재생 시작점으로 즉시 이동합니다.
    transitionRef.current = false
    if (pauseTimerRef.current) window.clearTimeout(pauseTimerRef.current)
    const startTime = continuousRange?.startTime ?? first.startTime
    const firstActive = continuousRange ? segnoAtTime(segnos, startTime) : first
    activeOrderRef.current = firstActive?.order ?? first.order
    onActiveOrder(firstActive?.order ?? first.order)
    player.seekTo(startTime, true)
    player.playVideo()
  }, [continuousRange, looping, onActiveOrder, queue, ready, segnos])

  useEffect(() => {
    if (!looping) {
      if (pollRef.current) window.clearInterval(pollRef.current)
      pollRef.current = null
      return
    }
    pollRef.current = window.setInterval(() => {
      const player = playerRef.current
      const currentQueue = queueRef.current
      const currentRange = continuousRangeRef.current
      const order = activeOrderRef.current
      if (!player || currentQueue.length === 0 || transitionRef.current) return

      if (currentRange) {
        const currentTime = player.getCurrentTime()
        const atTime = segnoAtTime(segnosRef.current, currentTime)
        if (atTime && atTime.order !== order) {
          activeOrderRef.current = atTime.order
          onActiveOrder(atTime.order)
        }
        if (currentTime >= currentRange.endTime - 0.08) {
          const firstActive = segnoAtTime(segnosRef.current, currentRange.startTime)
          const fallbackOrder = currentQueue[0].order
          activeOrderRef.current = firstActive?.order ?? fallbackOrder
          onActiveOrder(firstActive?.order ?? fallbackOrder)
          player.seekTo(currentRange.startTime, true)
          player.playVideo()
        }
        return
      }

      if (order == null) return
      const active = currentQueue.find((item) => item.order === order)
      if (active && player.getCurrentTime() >= active.endTime - 0.08) advance(active)
    }, 50)
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [looping])

  function advance(current: Segno) {
    const player = playerRef.current
    const currentQueue = queueRef.current
    if (!player || currentQueue.length === 0 || transitionRef.current) return
    transitionRef.current = true
    const index = Math.max(0, currentQueue.findIndex((item) => item.order === current.order))
    const next = currentQueue[(index + 1) % currentQueue.length]
    activeOrderRef.current = next.order
    onActiveOrder(next.order)

    if (isContiguous(current, next)) {
      player.seekTo(next.startTime, true)
      transitionRef.current = false
      player.playVideo()
      return
    }

    player.pauseVideo()
    pauseTimerRef.current = window.setTimeout(() => {
      player.seekTo(next.startTime, true)
      transitionRef.current = false
      player.playVideo()
    }, PAUSE_MS)
  }

  async function startLoop() {
    const player = playerRef.current
    if (!player || !ready || queue.length === 0) {
      setMessage(t('practice.segno.selectAtLeastOne'))
      return
    }
    transitionRef.current = false
    const first = queue[0]
    const startTime = continuousRange?.startTime ?? first.startTime
    const firstActive = continuousRange ? segnoAtTime(segnos, startTime) : first
    activeOrderRef.current = firstActive?.order ?? first.order
    onActiveOrder(firstActive?.order ?? first.order)
    player.seekTo(startTime, true)
    player.playVideo()
    setLooping(true)
    await requestWakeLock().catch(() => false)
  }

  function stop() {
    setLooping(false)
    transitionRef.current = false
    if (pauseTimerRef.current) window.clearTimeout(pauseTimerRef.current)
    playerRef.current?.pauseVideo()
    setMessage('')
  }

  function jump(seconds: number) {
    const player = playerRef.current
    if (!player) return
    const target = clampSeekTime(player.getCurrentTime(), player.getDuration(), seconds)
    player.seekTo(target, true)
  }

  return (
    <section className="youtube-layout card">
      {showGuidance && (
        <div className="youtube-ad-guidance notice warning">
          <strong>{t('practice.youtube.adNoticeTitle')}</strong>
          <span>{t('practice.youtube.adNoticeDescription')}</span>
          <button
            type="button"
            className="button secondary youtube-guidance-confirm"
            onClick={() => setShowGuidance(false)}
          >
            {t('common.confirm')}
          </button>
        </div>
      )}
      <div className={touchLocked ? 'youtube-shield-wrap touch-locked' : 'youtube-shield-wrap'}>
        <div ref={hostRef} title={title} />
        {touchLocked && <div className="youtube-shield" aria-label={t('practice.youtube.touchShield')} />}
        <button
          type="button"
          className={touchLocked ? 'youtube-touch-lock active' : 'youtube-touch-lock'}
          aria-pressed={touchLocked}
          onClick={() => setTouchLocked((current) => !current)}
        >
          {touchLocked ? t('practice.youtube.unlock') : t('practice.youtube.lock')}
        </button>
      </div>
      {compactPortrait && (
        <div className="youtube-portrait-essential-controls">
          <button
            type="button"
            className={looping
              ? 'button primary youtube-portrait-segno-action active'
              : 'button primary youtube-portrait-segno-action'}
            disabled={!ready || queue.length === 0}
            title={looping ? t('practice.sharedGuide.stopButton') : t('practice.sharedGuide.startButton')}
            aria-label={looping ? t('practice.sharedGuide.stopButton') : t('practice.sharedGuide.startButton')}
            aria-pressed={looping}
            onClick={() => looping ? stop() : void startLoop()}
          >
            <span aria-hidden="true">{looping ? '■' : '▶'}</span>
            <strong>{activeSegno?.title ?? t('practice.sharedGuide.selectSegmentPrompt')}</strong>
          </button>
        </div>
      )}
      <PlaybackSeekControls
        onSeek={jump}
        onPlay={startLoop}
        onStop={stop}
        disabled={!ready}
        playDisabled={!ready || queue.length === 0}
        playing={looping}
        className={compactPortrait ? 'youtube-seek-controls youtube-seek-controls-shared' : 'youtube-seek-controls'}
        playLabel={t('practice.action.startLoop')}
        stopLabel={t('practice.action.stopLoop')}
      />
      {message && <p className="notice youtube-player-message">{message}</p>}
    </section>
  )
})
